import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import * as LucideIcons from "lucide-react";
import {
  ChevronLeft, ChevronRight, Check, Loader2,
  AlertTriangle, ShieldCheck,
} from "lucide-react";
import { api } from "../lib/api";
import { useUser } from "../contexts/UserContext";
import PhotoUploader from "../components/PhotoUploader";
import RichTextToolbar from "../components/RichTextToolbar";
import RichText from "../components/RichText";
import VideoEmbed from "../components/VideoEmbed";
import { findVideo } from "../lib/media";

const SCOPES_BY_LEVEL = {
  ceo:        ["global","country","region","group","level","direction"],
  direction:  ["global","country","region","group","level","direction"],
  dn:         ["global","country","region","group","level","direction"],
  dr:         ["region","group","level"],
  consultant: ["group","level"],
  president:  ["global","region","group"],
};

const SCOPE_META = {
  global:    { label: "Monde entier", help: "Visible par tous les membres APOLLO.", icon: "Globe" },
  country:   { label: "Un pays",       help: "Visible par les membres de votre pays.", icon: "Flag" },
  region:    { label: "Une région",    help: "Visible par les membres de votre région.", icon: "Map" },
  group:     { label: "Un groupe",     help: "Visible par les membres de votre groupe primaire.", icon: "Users" },
  level:     { label: "Un niveau",     help: "Visible par les membres d'un niveau hiérarchique.", icon: "Layers" },
  direction: { label: "Direction",     help: "Visible uniquement par la Direction (CEO, DN, Direction).", icon: "ShieldCheck" },
};

const LEVEL_TARGETS_BY_AUTHOR = {
  ceo:        ["direction","dn","dr","consultant","president"],
  direction:  ["direction","dn","dr","consultant","president"],
  dn:         ["dr","consultant","president"],
  dr:         ["consultant","president"],
  consultant: ["president"],
  president:  [],
};

export default function Compose() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [quota, setQuota] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    category_id: "", title: "", content: "", original_language: "fr",
    target_scope: "", target_group_id: "", target_region_id: "", target_country_id: "",
    target_level: "", event_date: "", event_location: "", event_registration_url: "",
    external_url: "",
  });

  // Référence au <textarea> : la barre de mise en forme en a besoin pour
  // connaître la sélection courante et y réinjecter le markdown.
  const contentRef = useRef(null);

  // Vidéo YouTube / Vimeo citée dans le contenu (aperçu temps réel).
  // Déclaré AVANT le `if (!user) return null` ci-dessous : les hooks doivent
  // être appelés inconditionnellement.
  const composedVideo = useMemo(() => findVideo(form.content), [form.content]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/posts/quota/me").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  if (!user) return null;

  const cat = categories.find((c) => c.category_id === form.category_id);
  const allowedScopes = SCOPES_BY_LEVEL[user.hierarchy_level] || [];
  const levelTargets = LEVEL_TARGETS_BY_AUTHOR[user.hierarchy_level] || [];

  const pickScope = (s) => {
    const f = { ...form, target_scope: s, target_level: "" };
    if (s === "country") f.target_country_id = user.country_id;
    if (s === "region") f.target_region_id = user.region_id;
    if (s === "group") f.target_group_id = user.primary_group_id;
    setForm(f);
  };

  const canNext = () => {
    if (step === 1) return !!form.category_id;
    if (step === 2) {
      if (!form.target_scope) return false;
      if (form.target_scope === "level" && !form.target_level) return false;
      return true;
    }
    if (step === 3) {
      if (form.title.length < 3 || form.content.length < 10) return false;
      if (cat?.family === "event_training" && !form.event_date) return false;
      return true;
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.target_scope !== "country") payload.target_country_id = null;
      if (payload.target_scope !== "region") payload.target_region_id = null;
      if (payload.target_scope !== "group") payload.target_group_id = null;
      if (payload.target_scope !== "level") payload.target_level = null;
      // Normalisation : chaînes vides -> null pour tous les champs optionnels.
      // Évite les erreurs Postgres TIMESTAMPTZ ('') et les VARCHAR vides.
      for (const k of Object.keys(payload)) {
        if (typeof payload[k] === "string" && payload[k].trim() === "") {
          payload[k] = null;
        }
      }
      const { data } = await api.post("/posts", payload);
      const postId = data.post_id;
      // Upload photos après création
      if (photos.length > 0 && postId) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p.file));
        try {
          await api.post(`/posts/${postId}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (photoErr) {
          toast.error(
            "Post créé mais upload photos échoué : " +
              (photoErr?.response?.data?.detail || photoErr.message)
          );
        }
      }
      const status = data.status;
      if (status === "published") toast.success("Post publié 🎉");
      else toast.success("Post envoyé en modération. Vous serez notifié dès la décision.");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la publication.");
    } finally {
      setSubmitting(false);
    }
  };

  const quotaBlocked =
    quota && quota.limit != null && quota.remaining === 0;

  return (
    <div data-testid="compose-page">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1
          className="text-[28px] sm:text-[32px] font-bold leading-tight"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          Nouvelle publication
        </h1>
        {quota?.limit != null && (
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{
              background: quotaBlocked ? "#FEE2E2" : "#FFF7ED",
              color: quotaBlocked ? "#991B1B" : "#9A3412",
              border: `1px solid ${quotaBlocked ? "#FCA5A5" : "#FED7AA"}`,
            }}
            data-testid="compose-quota-badge"
          >
            <AlertTriangle size={12} />
            Quota : {quota.used}/{quota.limit} posts cette semaine
          </div>
        )}
      </div>
      <p className="mb-6 text-sm" style={{ color: "#666" }}>
        Étape {step}/5
      </p>

      {/* Stepper progress bar */}
      <div className="mb-6 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <motion.div
          className="h-full"
          style={{ background: "#F77F00" }}
          animate={{ width: `${(step / 5) * 100}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>

      {/* Quota blocked banner */}
      {quotaBlocked && (
        <div
          className="mb-4 p-4 rounded-lg flex items-start gap-3"
          style={{ background: "#FEF3F2", border: "1px solid #FECACA" }}
          data-testid="quota-blocked-banner"
        >
          <AlertTriangle size={18} color="#991B1B" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm" style={{ color: "#7F1D1D" }}>
            <strong>Quota hebdomadaire atteint.</strong> Vous pourrez à nouveau publier
            {quota.next_slot ? ` à partir du ${new Date(quota.next_slot).toLocaleString("fr-FR")}.` : "."}
          </div>
        </div>
      )}

      <div
        className="bg-white rounded-[12px] p-6 sm:p-8"
        style={{ border: "1px solid #E5E5E5" }}
      >
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="compose-step-category"
            >
              <h2 className="text-xl font-bold mb-5" style={{ fontFamily: "'Sora', sans-serif" }}>
                Choisissez une catégorie
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {categories
                  .filter(
                    (c) =>
                      !c.allowed_levels || c.allowed_levels.includes(user.hierarchy_level)
                  )
                  .map((c) => {
                    const Ic = LucideIcons[c.icon] || LucideIcons.Tag;
                    const sel = form.category_id === c.category_id;
                    return (
                      <button
                        key={c.category_id}
                        onClick={() => setForm({ ...form, category_id: c.category_id })}
                        data-testid={`compose-cat-${c.code}`}
                        className="p-4 rounded-xl text-left transition-all border-2 hover:-translate-y-px"
                        style={{
                          borderColor: sel ? c.color : "#E5E5E5",
                          background: sel ? `${c.color}10` : "#FFF",
                        }}
                      >
                        <Ic size={20} color={c.color} className="mb-2" />
                        <div className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>
                          {c.label_fr}
                        </div>
                        {c.description_fr && (
                          <div className="text-xs mt-1" style={{ color: "#666" }}>
                            {c.description_fr}
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="compose-step-target"
            >
              <h2 className="text-xl font-bold mb-5" style={{ fontFamily: "'Sora', sans-serif" }}>
                Qui doit voir ce post ?
              </h2>
              <div className="space-y-2">
                {allowedScopes.map((s) => {
                  const meta = SCOPE_META[s];
                  const Ic = LucideIcons[meta.icon] || LucideIcons.Globe;
                  const sel = form.target_scope === s;
                  return (
                    <button
                      key={s}
                      onClick={() => pickScope(s)}
                      data-testid={`compose-scope-${s}`}
                      className="w-full p-4 rounded-lg text-left transition-all border-2 flex items-start gap-3"
                      style={{
                        borderColor: sel ? "#F77F00" : "#E5E5E5",
                        background: sel ? "#FFF7ED" : "#FFF",
                      }}
                    >
                      <Ic size={18} color={sel ? "#F77F00" : "#666"} className="mt-0.5" />
                      <div>
                        <div className="font-semibold" style={{ color: "#1A1A1A" }}>
                          {meta.label}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "#666" }}>
                          {meta.help}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {form.target_scope === "level" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1.5">Niveau ciblé</label>
                  <select
                    value={form.target_level}
                    onChange={(e) => setForm({ ...form, target_level: e.target.value })}
                    className="w-full p-3 rounded-lg border"
                    style={{ borderColor: "#E5E5E5" }}
                    data-testid="compose-target-level-select"
                  >
                    <option value="">Choisissez un niveau…</option>
                    {levelTargets.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step-3"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="compose-step-content"
            >
              <h2 className="text-xl font-bold mb-5" style={{ fontFamily: "'Sora', sans-serif" }}>
                Votre contenu
              </h2>
              <label className="block text-sm font-medium mb-1.5">Titre</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={200}
                placeholder="Un titre clair et accrocheur"
                data-testid="compose-title-input"
                className="w-full p-3 rounded-lg border mb-1 outline-none focus:border-orange-500 transition-colors"
                style={{ borderColor: "#E5E5E5" }}
              />
              <div className="text-xs mb-4" style={{ color: "#999" }}>
                {form.title.length}/200
              </div>
              <label className="block text-sm font-medium mb-1.5">Contenu</label>
              <RichTextToolbar
                textareaRef={contentRef}
                value={form.content}
                onChange={(v) => setForm((f) => ({ ...f, content: v.slice(0, 2000) }))}
                disabled={submitting}
              />
              <textarea
                ref={contentRef}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                maxLength={2000}
                rows={8}
                placeholder="Partagez votre actualité, votre succès, vos idées…&#10;&#10;**gras**, *italique*, - listes, emoji 🎉&#10;Collez un lien YouTube ou Vimeo : la miniature s'affichera automatiquement."
                data-testid="compose-content-textarea"
                className="w-full p-3 rounded-lg border resize-none mb-1 outline-none focus:border-orange-500 transition-colors"
                style={{ borderColor: "#E5E5E5" }}
              />
              <div className="text-xs mb-4" style={{ color: "#999" }}>
                {form.content.length}/2000
              </div>

              {composedVideo && (
                <div className="mb-4" data-testid="compose-video-preview">
                  <label className="block text-sm font-medium mb-1.5">
                    Vidéo détectée
                  </label>
                  <VideoEmbed video={composedVideo} compact testid="compose-video-thumb" />
                  <p className="text-xs mt-1.5" style={{ color: "#999" }}>
                    Cette miniature illustrera le post dans le fil et sur LinkedIn.
                  </p>
                </div>
              )}

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1.5">
                  Photos (optionnel · max 4)
                </label>
                <PhotoUploader photos={photos} onChange={setPhotos} disabled={submitting} />
              </div>

              {cat?.family === "event_training" && (
                <div
                  className="mt-2 p-4 rounded-lg"
                  style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}
                >
                  <div className="text-sm font-semibold mb-3" style={{ color: "#9A3412" }}>
                    Informations événement
                  </div>
                  <label className="block text-sm font-medium mb-1.5">
                    Date et heure <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    data-testid="compose-event-date"
                    className="w-full p-3 rounded-lg border mb-3 outline-none focus:border-orange-500"
                    style={{ borderColor: "#E5E5E5" }}
                  />
                  <label className="block text-sm font-medium mb-1.5">Lieu</label>
                  <input
                    type="text"
                    value={form.event_location}
                    onChange={(e) => setForm({ ...form, event_location: e.target.value })}
                    data-testid="compose-event-location"
                    placeholder="Salle, ville, ou lien Zoom…"
                    className="w-full p-3 rounded-lg border mb-3 outline-none focus:border-orange-500"
                    style={{ borderColor: "#E5E5E5" }}
                  />
                  <label className="block text-sm font-medium mb-1.5">
                    URL d'inscription (optionnel)
                  </label>
                  <input
                    type="url"
                    value={form.event_registration_url}
                    onChange={(e) => setForm({ ...form, event_registration_url: e.target.value })}
                    placeholder="https://…"
                    className="w-full p-3 rounded-lg border outline-none focus:border-orange-500"
                    style={{ borderColor: "#E5E5E5" }}
                  />
                </div>
              )}
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="step-4"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="compose-step-preview"
            >
              <h2 className="text-xl font-bold mb-5" style={{ fontFamily: "'Sora', sans-serif" }}>
                Aperçu
              </h2>
              <div
                className="rounded-lg p-6 border-2"
                style={{ borderColor: "#E5E5E5", background: "#FAFAFA" }}
              >
                <div
                  className="text-xs font-semibold uppercase tracking-wider mb-2"
                  style={{ color: cat?.color }}
                >
                  {cat?.label_fr}
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>
                  {form.title}
                </h3>
                <div className="text-sm leading-relaxed" style={{ color: "#1A1A1A" }}>
                  <RichText>{form.content}</RichText>
                </div>
                {composedVideo && (
                  <div className="mt-4">
                    <VideoEmbed video={composedVideo} compact testid="compose-preview-video" />
                  </div>
                )}
                {form.event_date && (
                  <div className="mt-4 text-sm" style={{ color: "#666" }}>
                    📅 {new Date(form.event_date).toLocaleString("fr-FR")}
                    {form.event_location && ` — ${form.event_location}`}
                  </div>
                )}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg" style={{ background: "#FAFAFA" }}>
                  <div className="text-xs uppercase tracking-wide" style={{ color: "#999" }}>
                    Visibilité
                  </div>
                  <div className="font-semibold mt-1">
                    {SCOPE_META[form.target_scope]?.label}
                  </div>
                </div>
                <div className="p-3 rounded-lg" style={{ background: "#FAFAFA" }}>
                  <div className="text-xs uppercase tracking-wide" style={{ color: "#999" }}>
                    Catégorie
                  </div>
                  <div className="font-semibold mt-1">{cat?.label_fr}</div>
                </div>
              </div>
            </motion.div>
          )}

          {step === 5 && (
            <motion.div
              key="step-5"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              data-testid="compose-step-submit"
              className="text-center py-6"
            >
              <div
                className="w-16 h-16 rounded-full inline-flex items-center justify-center mb-4"
                style={{ background: "#D1FAE5" }}
              >
                <Check size={32} color="#10B981" />
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                Prêt à publier
              </h2>
              <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: "#666" }}>
                Selon votre niveau hiérarchique et la catégorie, votre post sera publié
                immédiatement ou soumis à modération. Vous serez notifié de la décision.
              </p>
              <div
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full mb-6"
                style={{ background: "#FFF7ED", color: "#9A3412" }}
              >
                <ShieldCheck size={12} />
                Auto-traduit en {user.language_preference?.toUpperCase() === "FR" ? "EN" : "FR"} dès publication
              </div>
              <div>
                <button
                  onClick={submit}
                  disabled={submitting || quotaBlocked}
                  data-testid="compose-submit-button"
                  className="px-8 py-3 rounded-full font-semibold text-white inline-flex items-center gap-2 disabled:opacity-50 transition-opacity hover:opacity-90"
                  style={{ background: "#F77F00", boxShadow: "0 6px 18px rgba(247,127,0,0.25)" }}
                >
                  {submitting && <Loader2 className="animate-spin" size={16} />}
                  {submitting ? "Envoi…" : "Publier maintenant"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div
          className="flex items-center justify-between mt-8 pt-6 border-t"
          style={{ borderColor: "#E5E5E5" }}
        >
          <button
            onClick={() => (step > 1 ? setStep(step - 1) : navigate("/"))}
            className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-black"
            style={{ color: "#666" }}
            data-testid="compose-back-button"
          >
            <ChevronLeft size={16} /> {step === 1 ? "Annuler" : "Précédent"}
          </button>
          {step < 5 && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              data-testid="compose-next-button"
              className="flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-40 transition-opacity hover:opacity-90"
              style={{ background: "#1A1A1A" }}
            >
              Suivant <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
